import { useContext } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'
import { BuilderContext } from '../BuilderContextInstance'
import { contextPathOf } from '../editable/contextFields'

/**
 * ReactiveJSON primitive — renders the display label of a value that may be
 * EITHER a Drupal taxonomy term-ref object (`{tid, vocabulary, name}`) OR a
 * plain string.
 *
 * Why it exists : a decoupled list can mix object instances whose same field
 * is a `term-ref` on one bundle (stored as the object above) and a `text`
 * field on another (stored as a string) — e.g. the Académie search lists
 * `academie_article` (category = term-ref) alongside `academie_immortel` /
 * `academie_evenement` (category = text). Binding `~.…category` directly then
 * crashes with React error #31 ("objects are not valid as a React child") on
 * the term-ref rows, while `~.…category.name` silently drops the string rows.
 * This primitive resolves both shapes to a single label.
 *
 * Resolution : object → `name` (then `label`, then `value`) ; scalar → itself.
 * Renders nothing for empty / null. It outputs bare text, so wrap it in the
 * styled element you want (it does not render its own tag).
 *
 * UNE SEULE EXCEPTION À « aucune balise » : l'ÉDITION d'un contenu dans son
 * modèle. Une catégorie non renseignée ne rendait alors rien du tout — donc
 * rien à cliquer, là où un titre vide montre au moins son filigrane, et le
 * conteneur ne peut pas prendre le relais (il n'a ni la liaison ni de quoi la
 * deviner). Dans ce cas — et dans CE CAS SEULEMENT — le texte est porté par un
 * `<span>` qui déclare la liaison : le canevas l'estampille comme les autres
 * (filet, filigrane tiré de l'intitulé du champ, tabulation) et le clic y ouvre
 * la saisie en place. La balise est là que la catégorie soit renseignée ou non :
 * sans elle, une catégorie deviendrait non modifiable AUSSITÔT choisie.
 * Hors édition d'objet — rendu public, édition de page — rien ne change : le
 * texte est nu, et pas de valeur veut dire pas de balise.
 *
 * YAML usage (inside a content array):
 *   - type: span
 *     attributes: { class: 'uppercase …' }
 *     content:
 *       - { type: TermLabel, value: ~.objectData.data.category }
 */
export const TermLabel = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  // Absent hors du builder (`useContext` rend alors `null`, jamais une erreur).
  const builder = useContext(BuilderContext)
  const value = evaluateTemplateValue({
    valueToEvaluate: props.value,
    globalDataContext,
    templateContext,
  })

  const label =
    value && typeof value === 'object'
      ? value.name ?? value.label ?? value.value ?? ''
      : value

  const contextPath = contextPathOf(props.value)
  const bound = contextPath && builder?.isEditMode && builder.editScope === 'object'
    ? builder.objectEdit?.resolve?.(contextPath)
    : null
  if (bound) {
    // Le `<span>` est la CIBLE de la saisie en place : c'est lui que le canevas
    // passe en contentEditable, et c'est son texte qui est remplacé. Vide, il
    // porte le filigrane du champ (CSS `:empty::before`).
    return <span data-builder-context-field={contextPath}>{label || null}</span>
  }
  if (label == null || label === '') return null
  return <>{label}</>
}
